// client/src/app/providers/FeatureFlagsProvider.jsx
import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { useConfig } from "./ConfigProvider.jsx";
import { parseFeatureFlags, flagsToString } from "@/shared/featureFlags";
import { updateCustomValueByFieldKey } from "@/api/customValues";

const CATALOG_KEY = "YG_FLAG_CATALOG";
const DEFAULT_KNOWN_FLAGS = ["reservations","settings","overview",
  "month","tables","staff","menu","ordering","realtime",
  "kds","kds.write","pos-lightspeed","pos-square","pos-micros","pos-pilot"
];

const Ctx = createContext({
  flagsSet: new Set(),
  has: () => false,
  all: [],
  catalog: [],
  toggle: () => {},
  setLocal: () => {},
  reset: () => {},
  save: async () => {},
  loading: false,
  dirty: false,
  error: null,
});

export function FeatureFlagsProvider({ children }) {
  const { config, setConfig } = useConfig();
  const source = String(config?.featureFlags || "reservations,month,settings");

  // enabled flags (editable)
  const [local, setLocal] = useState(() => parseFeatureFlags(source));

  // stable catalog of all flags we know about (enabled or not)
  const [catalog, setCatalog] = useState(() => {
    let stored = [];
    try { stored = JSON.parse(localStorage.getItem(CATALOG_KEY) || "[]"); } catch {}
    return new Set([...DEFAULT_KNOWN_FLAGS, ...parseFeatureFlags(source), ...stored]);
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // keep local and catalog in sync with config changes
  useEffect(() => {
    setLocal(parseFeatureFlags(source));
    setCatalog(prev => {
      const next = new Set([...prev, ...DEFAULT_KNOWN_FLAGS, ...parseFeatureFlags(source)]);
      try { localStorage.setItem(CATALOG_KEY, JSON.stringify([...next].sort())); } catch {}
      return next;
    });
  }, [source]);

  const flagsSet = local;
  const dirty = flagsToString(local) !== flagsToString(parseFeatureFlags(source));

  const addToCatalog = useCallback((flag) => {
    setCatalog(prev => {
      if (prev.has(flag)) return prev;
      const next = new Set(prev); next.add(flag);
      try { localStorage.setItem(CATALOG_KEY, JSON.stringify([...next].sort())); } catch {}
      return next;
    });
  }, []);

  const toggle = useCallback((flag) => {
    addToCatalog(flag); // ensure it stays visible even when off
    setLocal(prev => {
      const next = new Set(prev);
      next.has(flag) ? next.delete(flag) : next.add(flag);
      return next;
    });
  }, [addToCatalog]);

  const reset = useCallback(() => setLocal(parseFeatureFlags(source)), [source]);

  const save = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const value = flagsToString(local);
      setConfig({ featureFlags: value }); // instant UI update

      if (config.locationId) {
        await updateCustomValueByFieldKey(config.locationId, "feature_flags", value);
      }

      // keep catalog persisted (include whatever is in local even if newly added)
      setCatalog(prev => {
        const next = new Set([...prev, ...local]);
        try { localStorage.setItem(CATALOG_KEY, JSON.stringify([...next].sort())); } catch {}
        return next;
      });
    } catch (e) {
      setError(e);
      throw e;
    } finally {
      setLoading(false);
    }
  }, [local, setConfig, config.locationId]);

  const value = useMemo(() => ({
    flagsSet,
    has: (k) => flagsSet.has(k),
    all: Array.from(flagsSet).sort(),          // enabled only
    catalog: Array.from(catalog).sort(),       // show UI from this
    toggle,
    setLocal,
    reset,
    save,
    loading,
    dirty,
    error,
  }), [flagsSet, catalog, toggle, reset, save, loading, dirty, error]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useFeatureFlags() {
  return useContext(Ctx);
}
